import { useRoute, useRouter } from 'vue-router'
import { ref, computed } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

import { useAccountStore } from 'src/stores/account'

import {
  filterApprovals,
  getApprovalFilterFromQuery,
  getDefaultFilter,
  getDefaultSort,
  getDirectoryCounts,
  hasUnknownApprovalStatus,
} from 'src/services/helpers/approval.helper'
import { DEFAULT_FILTER_SORT } from 'src/services/consts/approval.const'
import {
  getSortAndPaginationFromQuery,
  isSameTableState,
  sanitizeQuery,
  sortItems,
  paginateItems,
} from 'src/services/app/util.service'

export const useApprovalsStore = defineStore('approvals', () => {
  const route = useRoute()
  const router = useRouter()
  const accountStore = useAccountStore()

  const filterBy = ref(getApprovalFilterFromQuery(route.query))
  const tableState = ref(getSortAndPaginationFromQuery(getDefaultSort(), route.query))

  // Computed values
  const allApprovals = computed(() => accountStore.accountData?.approvals || [])
  const totalApprovals = computed(() => allApprovals.value.length)
  const filteredApprovals = computed(() => {
    return filterApprovals(allApprovals.value, filterBy.value)
  })
  const approvalsToShow = computed(() => {
    const sorted = sortItems(
      filteredApprovals.value,
      tableState.value.sortBy,
      tableState.value.descending
    )
    return paginateItems(sorted, tableState.value.page, tableState.value.rowsPerPage)
  })
  const hasActiveFilters = computed(() => {
    return (
      !!filterBy.value.search ||
      filterBy.value.dateCreatedRange !== null ||
      filterBy.value.lastApprovedRange !== null
    )
  })

  // Categorized approvals - separated by active/revoked status
  const categorizedApprovals = computed(() => {
    const allFiltered = filterApprovals(allApprovals.value, {
      ...filterBy.value,
      status: null,
    })
    return allFiltered.reduce(
      (acc, approval) => {
        if (!approval.active) {
          acc.inactive.push(approval)
        } else {
          acc.active.push(approval)
        }
        return acc
      },
      { active: [], inactive: [] }
    )
  })
  const activeApprovals = computed(() => categorizedApprovals.value.active)
  const inactiveApprovals = computed(() => categorizedApprovals.value.inactive)
  const directoryCounts = computed(() => {
    return getDirectoryCounts(allApprovals.value)
  })

  function syncStateFromQuery(query) {
    filterBy.value = getApprovalFilterFromQuery(query)
    const statusNeedsFix = hasUnknownApprovalStatus(query)

    if (accountStore.isLoading || accountStore.error) return

    const totalItems = filteredApprovals.value.length
    const defaultSort = getDefaultSort()
    const nextTableState = getSortAndPaginationFromQuery(defaultSort, query, { totalItems })

    if (!isSameTableState(tableState.value, nextTableState)) {
      tableState.value = nextTableState
    }

    const urlPage = parseInt(query.page) || defaultSort.page
    if (statusNeedsFix || nextTableState.page !== urlPage) {
      const combinedParams = { ...filterBy.value, ...nextTableState }
      const cleanQuery = sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT)
      const approvalId = route.query.approvalId
      if (approvalId) {
        cleanQuery.approvalId = approvalId
      }
      router.replace({ query: cleanQuery })
    }
  }

  function resetFiltersState() {
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  function updateQuery(fieldsToUpdate) {
    // Updating the query will trigger the watch and update the state
    if (Object.keys(fieldsToUpdate).length === 0) return
    const shouldPushToHistory =
      fieldsToUpdate.status ||
      (fieldsToUpdate.page && fieldsToUpdate.page !== tableState.value.page)
    const combinedParams = {
      ...filterBy.value,
      ...tableState.value,
      ...fieldsToUpdate,
    }

    const approvalId = route.query.approvalId
    const cleanQuery = sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT)
    if (approvalId) {
      cleanQuery.approvalId = approvalId
    }

    const action = shouldPushToHistory ? 'push' : 'replace'
    router[action]({ query: cleanQuery })
  }

  function updateFilters(filterToUpdate) {
    updateQuery({ ...filterToUpdate, page: 1 })
  }

  function updateTableState(stateToUpdate) {
    updateQuery(stateToUpdate)
  }

  function clearFilters() {
    // Only clear filters and page - keeps sort and directory
    const currentStatus = filterBy.value.status
    const newFilter = { ...getDefaultFilter(), status: currentStatus }
    updateQuery({ ...newFilter, page: 1 })
  }

  function resetFilters() {
    // Resets everything
    updateQuery(DEFAULT_FILTER_SORT)
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  return {
    // State
    filterBy,
    tableState,
    activeDirectory: computed(() => filterBy.value.status || 'active'),

    // Computed
    allApprovals,
    filteredApprovals,
    approvalsToShow,
    totalApprovals,
    hasActiveFilters,
    categorizedApprovals,
    activeApprovals,
    inactiveApprovals,
    directoryCounts,

    // Actions
    syncStateFromQuery,
    resetFiltersState,
    updateQuery,
    updateTableState,
    updateFilters,
    clearFilters,
    resetFilters,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useApprovalsStore, import.meta.hot))
}
